import { useState, useMemo } from 'react';
import { Plus, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import { InsuranceCompanyList } from '../components/InsuranceCompanyList';
import { InsuranceCompanyForm } from '../components/InsuranceCompanyForm';
import { useInsuranceCompanies } from '../hooks/useInsuranceCompanies';
import { useAuth } from '@/hooks/useAuth';
import { Permissions } from '@/types';
import { deleteInsuranceCompany } from '../api/insurance';
import type { InsuranceCompany } from '../types';

export function InsuranceCompaniesPage() {
  const [showInactive, setShowInactive] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<InsuranceCompany | null>(null);

  const { hasPermission } = useAuth();
  const canCreateInsurance = hasPermission(Permissions.InsuranceCreate);
  const canUpdateInsurance = hasPermission(Permissions.InsuranceUpdate);

  // Fetch all companies or only active based on toggle
  const { data: companies, isLoading, error, refetch } = useInsuranceCompanies(
    showInactive ? undefined : { isActive: true }
  );

  // Filter companies by search query
  const filteredCompanies = useMemo(() => {
    if (!searchQuery.trim()) {
      return companies;
    }
    const query = searchQuery.toLowerCase();
    return companies.filter(
      (company) =>
        company.name.toLowerCase().includes(query) ||
        company.email?.toLowerCase().includes(query) ||
        company.phone?.includes(query)
    );
  }, [companies, searchQuery]);

  const handleAddCompany = () => {
    setSelectedCompany(null);
    setIsFormOpen(true);
  };

  const handleEditCompany = (company: InsuranceCompany) => {
    setSelectedCompany(company);
    setIsFormOpen(true);
  };

  const handleDeactivateCompany = async (company: InsuranceCompany) => {
    if (!window.confirm(`Are you sure you want to deactivate "${company.name}"?`)) {
      return;
    }

    try {
      await deleteInsuranceCompany(company.id);
      refetch();
    } catch (err) {
      const error = err as { response?: { data?: { message?: string } } };
      alert(error.response?.data?.message || 'Failed to deactivate company');
    }
  };

  const handleFormSuccess = () => {
    refetch();
  };

  return (
    <div className="min-h-screen bg-white px-5 py-6 space-y-4">
      {/* Header */}
      <div className="bg-white rounded-[10px] p-5 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-4">
          {/* Left side: Title */}
          <h2 className="text-lg font-semibold text-[#0a0a9e]">Insurance Companies</h2>

          {/* Right side: Search, Show Inactive, Add Button */}
          <div className="flex items-center gap-4">
            {/* Search Input */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search companies..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 w-[250px]"
              />
            </div>

            {/* Show Inactive Toggle */}
            <div className="flex items-center space-x-2">
              <Checkbox
                id="showInactive"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
              />
              <Label htmlFor="showInactive" className="text-sm font-normal cursor-pointer">
                Show inactive
              </Label>
            </div>

            {/* Add Button - requires create permission */}
            {canCreateInsurance && (
              <Button onClick={handleAddCompany} className="gap-2">
                <Plus className="h-4 w-4" />
                Add Company
              </Button>
            )}
          </div>
        </div>
      </div>

      <ErrorAlert error={error} className="rounded-[20px] p-4" />

      {/* Company List */}
      <div className="bg-white rounded-[20px] p-4 border border-[#d9d9d9]">
        <InsuranceCompanyList
          companies={filteredCompanies}
          onEdit={handleEditCompany}
          onDeactivate={handleDeactivateCompany}
          isLoading={isLoading}
          canEdit={canUpdateInsurance}
        />
      </div>

      {/* Add/Edit Company Form Dialog */}
      <InsuranceCompanyForm
        open={isFormOpen}
        onOpenChange={setIsFormOpen}
        company={selectedCompany}
        onSuccess={handleFormSuccess}
      />
    </div>
  );
}
